import type { Chat, MembershipPatch, Uuid } from '@liniya/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { queryKeys } from '../query-keys';
import { notify } from '../store/ui';

export function useChats() {
  return useQuery({
    queryKey: queryKeys.chats,
    queryFn: () => api.chats.list(),
  });
}

export function useChat(chatId: Uuid | null) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: chatId ? queryKeys.chat(chatId) : queryKeys.chat('none'),
    queryFn: () => api.chats.detail(chatId as Uuid),
    enabled: Boolean(chatId),
    // The list already holds everything except the member roster, so the
    // conversation can render immediately and fill in the rest.
    placeholderData: () =>
      queryClient.getQueryData<Chat[]>(queryKeys.chats)?.find((chat) => chat.id === chatId),
  });
}

/** Sort the way the server does: pinned first, then most recently active. */
export function sortChats(chats: Chat[]): Chat[] {
  return [...chats].sort((left, right) => {
    if (left.membership.is_pinned !== right.membership.is_pinned) {
      return left.membership.is_pinned ? -1 : 1;
    }
    return right.updated_at.localeCompare(left.updated_at);
  });
}

export function useUpdateMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, patch }: { chatId: Uuid; patch: MembershipPatch }) =>
      api.chats.updateMembership(chatId, patch),
    onSuccess: (chat) => {
      queryClient.setQueryData<Chat[]>(queryKeys.chats, (chats) =>
        chats?.map((item) => (item.id === chat.id ? { ...item, ...chat } : item)),
      );
      queryClient.setQueryData(queryKeys.chat(chat.id), chat);
    },
    onError: () => notify('Не удалось изменить настройки чата.', 'error'),
  });
}

export function useLeaveChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: Uuid) => api.chats.leave(chatId),
    onSuccess: (_result, chatId) => {
      queryClient.setQueryData<Chat[]>(queryKeys.chats, (chats) =>
        chats?.filter((chat) => chat.id !== chatId),
      );
      queryClient.removeQueries({ queryKey: queryKeys.messages(chatId) });
    },
    onError: () => notify('Не удалось покинуть чат.', 'error'),
  });
}

export function useClearHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: Uuid) => api.chats.clearHistory(chatId),
    onSuccess: async (_result, chatId) => {
      queryClient.removeQueries({ queryKey: queryKeys.messages(chatId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      notify('История очищена у вас');
    },
    onError: () => notify('Не удалось очистить историю.', 'error'),
  });
}

export function useBlockPeer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, blocked }: { chatId: Uuid; blocked: boolean }) =>
      api.chats.setBlocked(chatId, blocked),
    onSuccess: async (blocked, variables) => {
      queryClient.setQueryData<Chat[]>(queryKeys.chats, (chats) =>
        chats?.map((chat) =>
          chat.id === variables.chatId ? { ...chat, peer_is_blocked: blocked } : chat,
        ),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.chat(variables.chatId) });
      notify(blocked ? 'Пользователь заблокирован' : 'Пользователь разблокирован');
    },
    onError: () => notify('Не удалось изменить блокировку.', 'error'),
  });
}

export function useOpenDirectChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: Uuid) => api.chats.openDirect(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
    },
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: { title: string; description?: string; member_user_ids: Uuid[] }) =>
      api.chats.createGroup(draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
    },
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draft: { title: string; slug: string; description?: string }) =>
      api.chats.createChannel(draft),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
    },
  });
}
